import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisVertical,
  faFileInvoiceDollar,
  faCalculator,
} from "@fortawesome/free-solid-svg-icons";
import NoteIcon from "assets/icons/note.svg?react";
import Popper from "components/Popper";
import AddNoteForm from "components/AddNote/AddNoteForm";
import { SettleInvoiceForm } from "components/Budget/SettleInvoiceModal";
import InvoicePdfSlide from "components/CreateInvoiceRegistryModal/InvoicePdfSlide";
import ModalsManagerContext, {
  defaultValue,
  InvoiceRegistryModalOptions,
} from "contexts/ModalsManager";
import registryInvoicesService from "api/registryInvoicesService";
import { ENotificationType, notification } from "utils/notification";
import {
  bookingKeyOf,
  isInvoiceBooked,
  markInvoiceBooked,
} from "utils/bookingQueue";
import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

const MenuCell: FC<Props> = ({ element, reload, apiRef }) => {
  const { t } = useTranslation();
  const [showOptions, setShowOptions] = useState(false);
  const refWrapper = useRef<HTMLDivElement>(null);

  const [showNote, setShowNote] = useState(false);
  const [showSettle, setShowSettle] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);

  const { setModals, setOptions } = useContext(ModalsManagerContext);

  const handleCreateNote = () => {
    setShowOptions(false);
    setShowNote(true);
  };

  // Następna faktura do księgowania = pierwszy NIEzaksięgowany wiersz w bieżącej
  // kolejności (po sortach/filtrach) po fakturze `el`. Pomija już zaksięgowane
  // (bieżąca procedura). Zwraca callback albo undefined, gdy brak kolejnej.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const buildBookNext = (el: any): (() => void) | undefined => {
    const ids = apiRef.current?.getSortedRowIds?.() ?? [];
    const startIdx = ids.findIndex((id) => id === el?.Id);
    if (startIdx < 0) return undefined;
    for (let i = startIdx + 1; i < ids.length; i++) {
      const row = apiRef.current?.getRow(ids[i]);
      // Pomiń zaksięgowane: trwała flaga z BE (IsBooked) lub bieżąca sesja.
      if (!row || row.IsBooked || isInvoiceBooked(bookingKeyOf(row))) continue;
      return () => openAccountingFor(row);
    }
    return undefined;
  };

  // „Księgowanie": otwiera formularz „Faktura/Rachunek" (jak Faktura zakupowa),
  // bez sekcji pliku, z polami wypełnionymi danymi z faktury KSeF (XML).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const openAccountingFor = async (el: any) => {
    setShowOptions(false);

    const openForm = (prefill?: Awaited<
      ReturnType<typeof registryInvoicesService.getKsefAccountingPrefill>
    >) => {
      const options = new InvoiceRegistryModalOptions();
      options.accountingMode = true;
      options.prefill = prefill;
      options.invoice = el;
      options.onBookNext = buildBookNext(el);
      options.onBooked = () => markInvoiceBooked(bookingKeyOf(el));
      setOptions(options);
      setModals({ ...defaultValue, createInvoiceRegistryEntry: true, hide: false });
    };

    // Wiersze KSeF mają KsefNumber → pobieramy prefill z XML; pozostałe otwierają
    // formularz w trybie księgowania bez wstępnego wypełnienia.
    if (!el?.KsefNumber) {
      openForm(undefined);
      return;
    }

    try {
      setBookingLoading(true);
      const prefill = await registryInvoicesService.getKsefAccountingPrefill(
        el.KsefNumber,
      );
      openForm(prefill);
    } catch {
      notification({
        type: ENotificationType.ERROR,
        title: "Księgowanie",
        text: "Nie udało się pobrać danych faktury z KSeF.",
      });
    } finally {
      setBookingLoading(false);
    }
  };

  const handleAccounting = () => {
    if (bookingLoading) return;
    openAccountingFor(element);
  };

  const handleNewNote = () => {
    apiRef.current.updateRows([
      { id: element.Id, NotesCount: element.NotesCount + 1 },
    ]);
  };

  return (
    <div className={styles.cell} ref={refWrapper}>
      <div
        onClick={() => {
          setShowOptions(!showOptions);
        }}
        ref={refWrapper}
        className={styles.hover}
      >
        <FontAwesomeIcon icon={faEllipsisVertical} />
      </div>

      {
        <Popper
          open={showOptions}
          anchorEl={refWrapper?.current}
          setOpen={setShowOptions}
        >
          <div className={styles.dropdown}>
            <div
              onClick={handleAccounting}
              className={styles.dropdownItem}
              style={bookingLoading ? { opacity: 0.6, pointerEvents: "none" } : undefined}
            >
              <FontAwesomeIcon icon={faCalculator} />
              {bookingLoading ? "Pobieranie…" : "Księgowanie"}
            </div>
            <div
              onClick={() => {
                setShowOptions(false);
                setShowSettle(true);
              }}
              className={styles.dropdownItem}
            >
              <FontAwesomeIcon icon={faFileInvoiceDollar} />
              Rozlicz zaangażowanie
            </div>
            <div
              onClick={handleCreateNote}
              className={styles.dropdownItem}
            >
              <NoteIcon title={t("notes.addNote")} />
              {t("notes.addNote")}
            </div>
          </div>
        </Popper>
      }

      {showSettle && (
        <InvoicePdfSlide
          invoice={element}
          title={`Rozlicz zaangażowanie${element.InvoiceNumber ? ` — ${element.InvoiceNumber}` : ""}`}
          onClose={() => setShowSettle(false)}
        >
          <SettleInvoiceForm
            invoiceId={element.Id}
            invoiceNumber={element.InvoiceNumber}
            invoiceAmount={element.GrossAmount}
            onClose={() => setShowSettle(false)}
            hideHeader
          />
        </InvoicePdfSlide>
      )}

      {showNote && (
        <InvoicePdfSlide
          invoice={element}
          title="Dodaj notatkę"
          onClose={() => setShowNote(false)}
        >
          <AddNoteForm
            caseId={undefined}
            participantId={undefined}
            invoiceId={element.Id}
            defaultValues={undefined}
            refetch={reload}
            addNote={handleNewNote}
            toggle={() => setShowNote(false)}
          />
        </InvoicePdfSlide>
      )}
    </div>
  );
};

export default MenuCell;
